import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { SalesByDayItem } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { TrendingUp, BarChart3, LineChart, AreaChart } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface SalesByDayChartProps {
  data: SalesByDayItem[];
  period: "today" | "yesterday" | "week" | "month";
  onPeriodChange: (period: "today" | "yesterday" | "week" | "month") => void;
}

type ChartType = "bar" | "line" | "area";

export function SalesByDayChart({
  data,
  period,
  onPeriodChange,
}: SalesByDayChartProps) {
  const [chartType, setChartType] = useState<ChartType>("bar");

  const periods = [
    { key: "today" as const, label: "Hoje" },
    { key: "yesterday" as const, label: "Ontem" },
    { key: "week" as const, label: "Semana" },
    { key: "month" as const, label: "Mês" },
  ];

  const chartTypes = [
    { key: "area" as const, icon: AreaChart, label: "Área" },
    { key: "bar" as const, icon: BarChart3, label: "Barras" },
    { key: "line" as const, icon: LineChart, label: "Linha" },
  ];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const chartData = data.map((item) => ({
    ...item,
    dateFormatted: formatDate(item.date),
  }));

  // Calcular total do período
  const totalPeriod = chartData.reduce((acc, item) => acc + item.total, 0);
  const totalOrders = chartData.reduce((acc, item) => acc + item.count, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border border-gray-100 dark:border-gray-700 rounded-xl shadow-xl">
          <p className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{item.dateFormatted}</p>
          <p className="text-emerald-600 dark:text-emerald-400 font-bold text-lg">
            {Mask.formatCurrency(item.total)}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-xs">{item.count} pedidos</p>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 10, left: 0, bottom: 0 },
    };

    const xAxisProps = {
      dataKey: "dateFormatted",
      tick: { fontSize: 11, fill: '#9ca3af' },
      tickLine: false,
      axisLine: false,
      dy: 10,
    };

    const yAxisProps = {
      tick: { fontSize: 11, fill: '#9ca3af' },
      tickLine: false,
      axisLine: false,
      width: 60,
      tickFormatter: (value: number) =>
        value >= 1000 ? `R$${(value / 1000).toFixed(0)}k` : `R$${value}`,
    };

    switch (chartType) {
      case "line":
        return (
          <RechartsLineChart {...commonProps}>
            <defs>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="total"
              stroke="url(#lineGradient)"
              strokeWidth={3}
              dot={{ fill: '#3b82f6', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
            />
          </RechartsLineChart>
        );
      case "bar":
        return (
          <BarChart {...commonProps} barCategoryGap="20%">
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#6366f1" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }} />
            <Bar
              dataKey="total"
              fill="url(#barGradient)"
              radius={[6, 6, 0, 0]}
            />
          </BarChart>
        );
      default: // area
        return (
          <RechartsAreaChart {...commonProps}>
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                <stop offset="50%" stopColor="#6366f1" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="strokeGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="total"
              stroke="url(#strokeGradient)"
              strokeWidth={3}
              fill="url(#areaGradient)"
            />
          </RechartsAreaChart>
        );
    }
  };

  return (
    <Card className="h-full flex flex-col border-0 shadow-lg bg-white dark:bg-gray-900">
      <CardContent className="p-5 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/20">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Vendas por Dia</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Mask.formatCurrency(totalPeriod)} em {totalOrders} pedidos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tipo de gráfico */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {chartTypes.map((type) => (
                <button
                  key={type.key}
                  onClick={() => setChartType(type.key)}
                  className={`p-2 rounded-lg transition-all ${
                    chartType === type.key
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  }`}
                  title={type.label}
                >
                  <type.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            {/* Período */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {periods.map((p) => (
                <button
                  key={p.key}
                  onClick={() => onPeriodChange(p.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                    period === p.key
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 ? (
          <div className="flex-1 min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex-1 min-h-[220px] flex flex-col items-center justify-center">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-3">
              <BarChart3 className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma venda no período</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
